import React, { useState, useEffect, useContext, createContext } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { DINECENTRAL_AUTH, DINECENTRAL_FUNCTIONS } from "../Firebase.dinecentral";
import UserServiceDineCentral from "../service/UserService.dinecentral";
import { getRemoteEnv } from "../config/remoteEnv";

interface DineCentralAuthContextType {
  user: User | null;
  loading: boolean;
  agreeToMarketing: boolean | null;
  updateMarketingPreference: (value: boolean, userEmail?: string) => Promise<void>;
  processPendingMarketingPreference: (userEmail: string) => Promise<void>;
  verificationEmailSent: boolean;
  verificationCooldown: number;
  setVerificationEmailSent: (value: boolean) => void;
  startCooldownTimer: (seconds: number) => void;
}

const DineCentralAuthContext = createContext<DineCentralAuthContextType | null>(null);

export const useDineCentralAuth = () => {
  const context = useContext(DineCentralAuthContext);
  if (!context) {
    throw new Error("useDineCentralAuth must be used within a DineCentralAuthProvider");
  }
  return context;
};

// ── localStorage helpers ──────────────────────────────────────────────────────

export const savePendingMarketingPreference = (email: string, marketing: boolean) => {
  try {
    const existing = localStorage.getItem("dc_marketingSignUps");
    const data = existing ? JSON.parse(existing) : {};
    data[email] = { marketing, fulfilled: false };
    localStorage.setItem("dc_marketingSignUps", JSON.stringify(data));
  } catch (e) {
    console.error("Error saving pending marketing preference:", e);
  }
};

const checkPendingMarketingPreference = (email: string): boolean => {
  try {
    const existing = localStorage.getItem("dc_marketingSignUps");
    if (!existing) return false;
    const data = JSON.parse(existing);
    const prefs = data[email];
    return prefs?.marketing === true && prefs?.fulfilled === false;
  } catch {
    return false;
  }
};

const markMarketingPreferenceFulfilled = (email: string) => {
  try {
    const existing = localStorage.getItem("dc_marketingSignUps");
    if (!existing) return;
    const data = JSON.parse(existing);
    if (data[email]) {
      data[email].fulfilled = true;
      localStorage.setItem("dc_marketingSignUps", JSON.stringify(data));
    }
  } catch (e) {
    console.error("Error marking marketing preference fulfilled:", e);
  }
};

// ── Provider ──────────────────────────────────────────────────────────────────

type Props = { children: React.ReactNode };

export const DineCentralAuthProvider = (props: Props) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [agreeToMarketing, setAgreeToMarketing] = useState<boolean | null>(null);

  const [verificationEmailSent, setVerificationEmailSent] = useState<boolean>(
    localStorage.getItem("dc_verificationEmailSent") === "true"
  );
  const [verificationCooldown, setVerificationCooldown] = useState<number>(0);

  // ── Cooldown timer ──────────────────────────────────────────────────────────

  const startCooldownTimer = (seconds: number) => {
    if (seconds <= 0) {
      setVerificationCooldown(0);
      return;
    }
    setVerificationCooldown(seconds);
    localStorage.setItem("dc_verificationCooldownExpires", (Date.now() + seconds * 1000).toString());
    const timer = setTimeout(() => startCooldownTimer(seconds - 1), 1000);
    return () => clearTimeout(timer);
  };

  // Restore cooldown on mount
  useEffect(() => {
    const expires = localStorage.getItem("dc_verificationCooldownExpires");
    if (!expires) return;
    const remaining = Math.ceil((parseInt(expires) - Date.now()) / 1000);
    if (remaining > 0) {
      startCooldownTimer(remaining);
      setVerificationEmailSent(true);
    } else {
      localStorage.removeItem("dc_verificationCooldownExpires");
      localStorage.removeItem("dc_verificationEmailSent");
      setVerificationEmailSent(false);
    }
  }, []);

  // ── Marketing preference ────────────────────────────────────────────────────

  const updateMarketingPreference = async (value: boolean, userEmail?: string): Promise<void> => {
    if (!userEmail && !user) return;
    try {
      const setMarketingPermissions = httpsCallable(DINECENTRAL_FUNCTIONS, "setMarketingPermissions");
      await setMarketingPermissions({
        appId: getRemoteEnv().appId,
        agreeToMarketing: value,
      });
      setAgreeToMarketing(value);
    } catch (error) {
      console.error("DineCentral: Error updating marketing preference:", error);
      throw error;
    }
  };

  const processPendingMarketingPreference = async (userEmail: string): Promise<void> => {
    if (!checkPendingMarketingPreference(userEmail)) return;
    try {
      await updateMarketingPreference(true, userEmail);
      markMarketingPreferenceFulfilled(userEmail);
      console.log(`DineCentral: Marketing preference processed for ${userEmail}`);
    } catch (error) {
      console.error("DineCentral: Error processing pending marketing preference:", error);
    }
  };

  // ── Auth state listener ─────────────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(DINECENTRAL_AUTH, async (firebaseUser) => {
      if (firebaseUser) {
        await firebaseUser.reload();
        const refreshed = DINECENTRAL_AUTH.currentUser;
        setUser(refreshed);

        if (refreshed?.email) {
          // If verified and pending marketing preference, fire it now
          if (refreshed.emailVerified && checkPendingMarketingPreference(refreshed.email)) {
            await processPendingMarketingPreference(refreshed.email);
          } else {
            // Otherwise just read current preference from Firestore
            try {
              const userData = await UserServiceDineCentral.GetUserData(refreshed);
              setAgreeToMarketing(userData.agreeToMarketing ?? null);
            } catch (e) {
              console.error("DineCentral: Error fetching user data:", e);
            }
          }
        }
      } else {
        setUser(null);
        setAgreeToMarketing(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <DineCentralAuthContext.Provider
      value={{
        user,
        loading,
        agreeToMarketing,
        updateMarketingPreference,
        processPendingMarketingPreference,
        verificationEmailSent,
        verificationCooldown,
        setVerificationEmailSent,
        startCooldownTimer,
      }}
    >
      {props.children}
    </DineCentralAuthContext.Provider>
  );
};